import { t } from 'i18next';
import { IMembershipPlan, IMembershipPlanPrice } from '../../types/model';
import { useQuery, useMutation } from '@apollo/client';
import {
  CHECKOUT_START,
  CHECKOUT_COMPLETE,
  GET_MEMBERSHIP_PLAN,
} from '../../http/apollo-queries';
import { FooterNavigator } from '../../components/Footer/FooterNavigator';
import { Icon, Loading } from '../../components/UI';
import { useAuth } from '../../context/Auth';
import { useState } from 'react';
import { Chargebee, CheckoutStartResponse, CheckoutProps } from './types';
import { useNavigate } from 'react-router';
import { logger } from '../../helpers/logger';

const SwagBag = ({
  membershipPlanId,
  action,
  setIsModalOpen,
}: CheckoutProps) => {
  const { user, refetch: refetchUser } = useAuth();
  const navigate = useNavigate();

  const [selectedPrice, setSelectedPrice] = useState(
    {} as IMembershipPlanPrice
  );
  const [checkoutCompleted, setCheckoutCompleted] = useState<boolean>(false);

  const chargebee = window['Chargebee'] as Chargebee;

  const cbInstance = chargebee.init({
    site: process.env.REACT_APP_CHARGEBEE_SITE,
    publishableKey: process.env.REACT_APP_CHARGEBEE_KEY,
  });

  // console.log('Checkout:cbInstance', cbInstance);

  const { data: { getMembershipPlan: membershipPlan } = {}, loading } =
    useQuery<{
      getMembershipPlan: IMembershipPlan;
    }>(GET_MEMBERSHIP_PLAN, {
      variables: {
        id: membershipPlanId,
      },
      onCompleted: (res) => {
        console.log('Checkout:getMembershipPlan', res.getMembershipPlan.title);
        setSelectedPrice(res.getMembershipPlan.prices[0]);
      },
    });

  const [checkoutStart, { loading: checkoutStartLoading }] =
    useMutation<CheckoutStartResponse>(CHECKOUT_START);

  const [checkoutComplete, { loading: checkoutCompleteLoading }] =
    useMutation(CHECKOUT_COMPLETE);

  const handleCheckout = () => {
    logger.log('cbInstance', cbInstance);

    cbInstance.openCheckout({
      hostedPage: () => {
        setCheckoutCompleted(false);
        return new Promise((resolve, reject) => {
          checkoutStart({
            variables: {
              input: {
                email: user!.email,
                priceSku: selectedPrice!.sku,
              },
            },
          })
            .then(({ data }) => {
              return resolve(data?.checkoutStart);
            })
            .catch((err) => {
              return reject(err);
            });
        });
      },
      success(hostedPageId) {
        logger.log('Checkout:hostedPage:success', hostedPageId);

        checkoutComplete({
          variables: {
            input: {
              hostPageId: hostedPageId,
              membershipId: user!.membership.id,
              membershipPlanId: membershipPlanId,
              priceSku: selectedPrice!.sku,
            },
          },
        })
          .then(() => refetchUser())
          .then(() => {
            setCheckoutCompleted(true);
          })
          .catch((err) => logger.error(err));
      },
      close: () => {
        if (checkoutCompleted) {
          navigate('/checkout-success');
        }
        setIsModalOpen(false);
      },
      error: () => {
        navigate('/checkout-error');
      },
    });
  };
  logger.log('Checkout:membershipPlan', membershipPlan);

  return (
    <>
      {loading ? (
        <Loading className={'mx-auto mt-16'} status={true} />
      ) : (
        <>
          <div className={'flex flex-col w-full h-full overflow-auto'}>
            <div className={'w-full bg-lime px-8 py-12'}>
              <div className={'pb-2 mb-2 text-center'}>
                <div className={'text-2xl font-semibold mb-2'}>
                  {t('checkout.features.label.swagBag')}
                </div>
              </div>

              <div className={'flex justify-center'}>
                {membershipPlan?.title === 'Lancelot' ? (
                  <img src="swagbag_lancelot.png" alt="swagbag_lancelot" />
                ) : membershipPlan?.title === 'Excalibur' ? (
                  <img src="swagbag_excalibur.png" alt="swagbag_excalibur" />
                ) : (
                  <img src="swagbag_king_arthur.png" alt="swagbag_Arthur" />
                )}
              </div>
            </div>

            <div>
              <div
                className={
                  'w-full text-center text-lg font-semibold mb-8 py-4 text-black'
                }
              >
                {t('checkout.includes')}
              </div>

              <div
                className={
                  'inline-grid grid-cols-2 xl:px-64 lg:px-16 px-8 p-2 w-full'
                }
              >
                {(membershipPlan?.features?.swagBag || []).map(
                  (feature, index) => (
                    <div className={'mb-8'} key={feature}>
                      <div className={'text-center mb-2 '}>
                        <Icon
                          svgName={'check'}
                          className={'mx-auto w-6 h-6 bg-lime rounded-full p-1'}
                        />
                      </div>

                      <div className={'font-semibold text-lg text-center mb-2'}>
                        {t('checkout.features.swagBag.' + feature)}
                      </div>

                      <div className={'text-center text-grey-50'}>
                        {t('checkout.features.swagBagDescription.' + feature)}
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
          <FooterNavigator
            className={'w-full'}
            backButtonClassName={'border-none'}
            saveButtonName={
              checkoutCompleted
                ? t('checkout.close')
                : t(
                    `checkout.${
                      action === 'upgrade' ? 'upgradeTo' : 'downgradeTo'
                    }`,
                    { plan: membershipPlan?.title }
                  )
            }
            isDisabledSaveButton={
              selectedPrice?.sku === user?.membership.sku ||
              (user?.membership?.source === 'MOBILE' && user?.membership?.paid)
            }
            onSave={() => {
              checkoutCompleted ? setIsModalOpen(false) : handleCheckout();
            }}
            onBack={() => {
              setIsModalOpen(false);
            }}
            loading={checkoutStartLoading || checkoutCompleteLoading}
            oneButton={true}
          />
        </>
      )}
    </>
  );
};
export default SwagBag;
